/* eslint-disable react/forbid-foreign-prop-types */
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import "@xfrontend/xui-react-plus/lib/styles/main.css";
import "@xfrontend/cycraft-chart/lib/styles/main.css";
import "./index.scss";
import { AuthProvider } from "react-oidc-context";
import { GoogleAuthProvider } from "./modules/auth/googleAuth";
import { ApexLineChart } from "@xfrontend/cycraft-chart";
import Accordion from "@xfrontend/xui-react-plus/lib/esm/components/accordion/Accordion";
import { I18nextProvider } from "react-i18next";
import { authConfig, isGoogle } from "./modules/auth/idp";
import App from "./infra/App";
import { store } from "./infra/store/instance";
import { LanguageProvider } from "./i18n/LanguageContext";
import { OrgProvider } from "./modules/auth/OrgContext";
import i18n from "./i18n";

// Hotfix for @xfrontend/cycraft-chart library bug
// The library defines yaxis propType incorrectly as `object || array` which evaluates to just `object`.
// We override it at runtime to allow array types, suppressing the false positive warning.

// Hotfix for @xfrontend/xui-react-plus library bug
// AccordionBody defines defaultProps.duration as number (350) but propTypes.duration as string.
// This causes a warning even when using default behavior. We override it to allow any type.

const originalError = console.error;
console.error = (...args) => {
  // Filter out specific PropType warnings that are known false positives or vendor issues
  const msg = args.map((a) => String(a)).join(" ");
  if (msg.includes("Support for defaultProps will be removed")) return;

  originalError.apply(console, args);
};
if (ApexLineChart.propTypes) {
  ApexLineChart.propTypes.yaxis = () => null; // Allow any type (effectively PropTypes.any)
}
if (Accordion.Body && Accordion.Body.propTypes) {
  Accordion.Body.propTypes.duration = () => null;
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container missing");
}

const root = createRoot(container);

// Shared by both providers so the tree below is written once.
const tree = (
  <I18nextProvider i18n={i18n}>
    <LanguageProvider>
      <OrgProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </OrgProvider>
    </LanguageProvider>
  </I18nextProvider>
);

// The provider must match whatever modules/auth/useAuth dispatches to — both
// read the same build-time `isGoogle`, which is what keeps them in step. Google
// cannot use <AuthProvider>: see googleAuth.tsx for why the authorization-code
// flow is unavailable to a browser client against Google.
root.render(
  <Provider store={store}>
    {isGoogle ? (
      <GoogleAuthProvider>{tree}</GoogleAuthProvider>
    ) : (
      <AuthProvider
        {...authConfig}
        onSigninCallback={() => {
          // Strip the ?code/&state (and the /callback path) after the OIDC redirect,
          // so the user lands on "/" instead of a blank /callback page.
          window.history.replaceState({}, document.title, "/");
        }}
      >
        {tree}
      </AuthProvider>
    )}
  </Provider>,
);
